import momentTz from 'moment-timezone/builds/moment-timezone-with-data-10-year-range';
import { createJsonAdapter } from './jsonAdapter';

export interface IGeofenceRow {
  id: string;
  name: string;
  address?: string | null;
  lat: number;
  lng: number;
  radiusMeters?: number | null;
  alertOnEnter?: boolean;
  alertOnExit?: boolean;
  isActive?: boolean;
  noShowEnabled?: boolean;
  noShowRecurrenceType?: string | null;
  noShowStartAt?: string | null;
  noShowRepeatWeekOn?: number[] | null;
}

export const NO_SHOW_RECURRENCE_TYPES = ['ONCE', 'DAILY', 'WEEKLY'];

const DATE = 'YYYY-MM-DD';
const TIME = 'HH:mm';

const str = (value: unknown) => String(value ?? '').trim();
const num = (value: unknown) => Number(value);

// "Alert if not arrived by...": the date/time are the senior's local ones, stored as one UTC instant
// (noShowStartAt), exactly as the app does - so `timezone` is the senior's, not the browser's.
export const createGeofenceAdapter = (timezone: string) =>
  createJsonAdapter<IGeofenceRow>({
    url: 'geofence-zones',
    rowToForm: (row) => {
      const start = row.noShowStartAt ? momentTz.tz(row.noShowStartAt, timezone) : undefined;

      return {
        name: row.name ?? '',
        address: row.address ?? '',
        lat: row.lat ?? '',
        lng: row.lng ?? '',
        radiusMeters: row.radiusMeters ?? '',
        alertOnEnter: row.alertOnEnter ?? false,
        alertOnExit: row.alertOnExit ?? false,
        isActive: row.isActive ?? true,
        noShowEnabled: row.noShowEnabled ?? false,
        noShowRecurrenceType: row.noShowRecurrenceType ?? 'DAILY',
        noShowDate: (start ?? momentTz.tz(timezone)).format(DATE),
        noShowTime: start?.format(TIME) ?? '',
        // Sunday = 0 .. Saturday = 6
        noShowRepeatWeekOn: (row.noShowRepeatWeekOn ?? []).join(','),
      };
    },
    toBody: (form) => {
      const noShow =
        form.noShowEnabled === true
          ? {
              noShowEnabled: true,
              noShowRecurrenceType: str(form.noShowRecurrenceType),
              noShowStartAt: momentTz
                .tz(`${str(form.noShowDate) || momentTz.tz(timezone).format(DATE)} ${str(form.noShowTime)}`, `${DATE} ${TIME}`, timezone)
                .toISOString(),
              ...(form.noShowRecurrenceType === 'WEEKLY'
                ? { noShowRepeatWeekOn: str(form.noShowRepeatWeekOn).split(',').filter(Boolean).map(Number) }
                : {}),
            }
          : { noShowEnabled: false };

      return {
        name: str(form.name),
        address: str(form.address),
        lat: num(form.lat),
        lng: num(form.lng),
        ...(str(form.radiusMeters) ? { radiusMeters: num(form.radiusMeters) } : {}),
        alertOnEnter: form.alertOnEnter === true,
        alertOnExit: form.alertOnExit === true,
        isActive: form.isActive !== false,
        ...noShow,
      };
    },
  });

export const getDefaultNoShowDate = (timezone: string) => momentTz.tz(timezone).format(DATE);

// "z" abbreviation (e.g. "+07") for the time label, as in the app
export const getTimezoneAbbr = (timezone: string) => momentTz.tz(timezone).format('z');
